import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import useIsMobile from "@/hooks/useIsMobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DotsThreeVertical, Trash, Plus, CaretRight, CaretDown, TreeStructure, Eye, DotsSixVertical, UsersThree } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { getStatuses, statusPillStyle, workstreamColor } from "@/lib/gantt-utils";
import { formatDate, toIsoDate } from "@/lib/dates";
import { useTheme } from "@/lib/theme";
import DependencyPicker from "./DependencyPicker";

// Sticky-cell backgrounds need explicit hex (inline styles override CSS classes
// because position:sticky requires solid fills). These match the global dark
// CSS overrides in index.css so the pinned columns blend in either mode.
const ROW_BG = {
  light: { default: "#ffffff", subtask: "#f8fafc", critical: "#fef2f2", flash: "#fffbeb", selected: "#f1f5f9" },
  dark:  { default: "#15101a", subtask: "#1a1420", critical: "#2d1414", flash: "#3a2d10", selected: "#221a2a" },
};

export const BASE_COLS = [
  { key: "no", label: "No.", defaultWidth: 46, minWidth: 36, align: "center", required: true },
  { key: "task", label: "Task", defaultWidth: 180, minWidth: 100, required: true },
  { key: "subtask", label: "Subtask", defaultWidth: 220, minWidth: 100, required: true },
  { key: "workstream", label: "Workstream", defaultWidth: 130, minWidth: 80 },
  { key: "status", label: "Status", defaultWidth: 140, minWidth: 80 },
  { key: "dependencies", label: "Dependencies", defaultWidth: 200, minWidth: 100 },
];

const SELECT_COL_WIDTH = 34;
const ACTION_COL_WIDTH = 36;

export default function TaskTable({
  tasks,
  allTasks,
  allTasksCount,
  project,
  canEdit,
  onUpdateTask,
  onDeleteTask,
  onAddTask,
  onAddSubtask,
  scrollRef,
  onScroll,
  flashTaskId,
  criticalIds,
  collapsed,
  onToggleCollapse,
  childrenByParent,
  selectMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  colWidths,
  hiddenCols,
  onResizeColumn,
  onRenameColumn,
  onRenameDateColumn,
  onOpenDetail,
  onReorderRows,
  meetingMarkersByTask = {},}) {
  const dateCols = (project.date_columns || []).filter((c) => !hiddenCols?.has(`date:${c.id}`));
  const customCols = (project.custom_columns || []).filter((c) => !hiddenCols?.has(`custom:${c.id}`));
  const labelOverrides = project.column_labels || {};
  const statuses = getStatuses(project);
  // On narrow viewports (mobile), pinning all three identity columns
  // (No + Task + Subtask = ~446px) eats more space than the viewport itself,
  // which makes the secondary columns (Workstream/Status/Dates) impossible
  // to scroll into view — they stay parked off the right edge no matter how
  // far the user scrolls. Below 768px we keep ONLY "No." pinned so the
  // remaining columns can actually be reached via horizontal scroll.
  const isMobile = useIsMobile(768);
  const STICKY_KEYS = isMobile ? new Set(["no"]) : new Set(["no", "task", "subtask"]);
  const visibleBase = BASE_COLS.filter((c) => !hiddenCols?.has(c.key));

  const widthFor = (key, fallback) => {
    const w = colWidths?.[key];
    return Number.isFinite(w) && w > 0 ? w : fallback;
  };

  // Build cumulative sticky-left offsets for the first group of visible sticky columns.
  // These stop accumulating once a non-sticky column appears in the visible order.
  const stickyLeftByKey = {};
  const selectOffset = selectMode ? SELECT_COL_WIDTH : 0;
  let runningLeft = selectOffset;
  for (const col of visibleBase) {
    if (!STICKY_KEYS.has(col.key)) break;
    stickyLeftByKey[col.key] = runningLeft;
    runningLeft += widthFor(col.key, col.defaultWidth);
  }
  const stickyEndLeft = runningLeft; // right edge of the last sticky column, for shadow overlay

  const labelFor = (key, fallback) => labelOverrides[key] || fallback;

  const totalWidth =
    visibleBase.reduce((s, c) => s + widthFor(c.key, c.defaultWidth), 0) +
    dateCols.reduce((s, c) => s + widthFor(`date:${c.id}`, 110), 0) +
    customCols.reduce((s, c) => s + widthFor(`custom:${c.id}`, 140), 0) +
    ACTION_COL_WIDTH +
    (selectMode ? SELECT_COL_WIDTH : 0);

  // Build numbering map (1, 2, 1.1, 1.2, etc.) — also exposed to children (DependencyPicker)
  const numberingById = new Map();
  const childCountByParent = new Map();
  let topIdx = 0;
  const childCursor = {};
  for (const t of tasks) {
    if (!t.parent_id) {
      topIdx += 1;
      numberingById.set(t.id, String(topIdx));
    } else {
      childCursor[t.parent_id] = (childCursor[t.parent_id] || 0) + 1;
      const parentNo = numberingById.get(t.parent_id);
      const label = parentNo ? `${parentNo}.${childCursor[t.parent_id]}` : `↳ ${childCursor[t.parent_id]}`;
      numberingById.set(t.id, label);
    }
  }
  if (childrenByParent) {
    for (const [pid, kids] of childrenByParent) {
      childCountByParent.set(pid, kids.length);
    }
  }

  const allSelected =
    selectMode &&
    tasks.length > 0 &&
    tasks.every((t) => selectedIds?.has(t.id));
  const someSelected =
    selectMode && !allSelected && tasks.some((t) => selectedIds?.has(t.id));

  // Build display numbering: 1, 2, 3 for top-level; 1.1, 1.2 for subtasks.
  // (Already computed above as `numberingById`/`childCountByParent`.)
  const numbering = numberingById;

  // Theme-aware sticky backgrounds. Inline styles bypass our html.dark CSS so
  // we have to read the theme directly and pick the right hex per row state.
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const palette = isDark ? ROW_BG.dark : ROW_BG.light;

  return (
    <div
      className="bg-white dark:bg-[#15101a] flex flex-col h-full overflow-hidden border-r border-slate-300 relative"
      data-testid="task-table"
    >
      <div className="h-[73px] border-b border-slate-300 shrink-0 overflow-hidden bg-white dark:bg-[#15101a] z-10 relative">
        <div className="overflow-x-auto overflow-y-hidden h-full custom-scrollbar" id="table-header-scroll">
          <div
            className="flex text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 font-medium h-full"
            style={{ minWidth: totalWidth }}
          >
            {selectMode && (
              <div
                className="flex items-center justify-center px-2 border-r border-slate-200"
                style={{ width: SELECT_COL_WIDTH, flexShrink: 0, position: "sticky", left: 0, zIndex: 3, background: palette.default }}
              >
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={(v) => onToggleSelectAll?.(!!v)}
                  aria-label="Select all visible tasks"
                  className="h-3.5 w-3.5 rounded-[3px]"
                  data-testid="select-all-checkbox"
                />
              </div>
            )}
            {visibleBase.map((col) => {
              const w = widthFor(col.key, col.defaultWidth);
              const stickyLeft = stickyLeftByKey[col.key];
              const isSticky = stickyLeft != null;
              const isLastSticky = isSticky && !stickyLeftByKey[visibleBase[visibleBase.indexOf(col) + 1]?.key];
              const renamable = !col.required && onRenameColumn;
              return (
                <ResizableHeader
                  key={col.key}
                  width={w}
                  minWidth={col.minWidth}
                  align={col.align}
                  onResize={(nw) => onResizeColumn?.(col.key, nw)}
                  testid={`col-resize-${col.key}`}
                  stickyLeft={stickyLeft}
                  shadowOnRight={isLastSticky}
                  onRename={renamable ? (v) => onRenameColumn(col.key, v) : null}
                  renameTestid={renamable ? `col-rename-${col.key}` : null}
                  bg={palette.default}
                  isDark={isDark}
                >
                  {labelFor(col.key, col.label)}
                </ResizableHeader>
              );
            })}
            {dateCols.map((col) => {
              const w = widthFor(`date:${col.id}`, 110);
              return (
                <ResizableHeader
                  key={col.id}
                  width={w}
                  minWidth={70}
                  onResize={(nw) => onResizeColumn?.(`date:${col.id}`, nw)}
                  testid={`col-resize-date-${col.id}`}
                  flexCol
                  onRename={onRenameDateColumn ? (v) => onRenameDateColumn(col.id, v) : null}
                  renameTestid={`col-rename-date-${col.id}`}
                  bg={palette.default}
                  isDark={isDark}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2"
                      style={{
                        background: col.color,
                        transform: col.shape === "diamond" ? "rotate(45deg)" : "none",
                        borderRadius: col.shape === "flag" ? "0" : col.shape === "dot" ? "999px" : "2px",
                      }}
                    />
                    <span>{col.label}</span>
                  </div>
                </ResizableHeader>
              );
            })}
            {customCols.map((col) => {
              const w = widthFor(`custom:${col.id}`, 140);
              return (
                <ResizableHeader
                  key={`custom-${col.id}`}
                  width={w}
                  minWidth={80}
                  onResize={(nw) => onResizeColumn?.(`custom:${col.id}`, nw)}
                  testid={`col-resize-custom-${col.id}`}
                  bg={palette.default}
                  isDark={isDark}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm bg-slate-400" />
                    <span>{col.label}</span>
                    <span className="text-[9px] font-mono-ibm text-slate-400 uppercase tracking-widest">
                      {col.type}
                    </span>
                  </div>
                </ResizableHeader>
              );
            })}
            <div className="w-9 shrink-0 border-r border-slate-200" style={{ width: ACTION_COL_WIDTH }} />
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => {
          onScroll?.(e);
          const header = document.getElementById("table-header-scroll");
          if (header) header.scrollLeft = e.target.scrollLeft;
        }}
        className="flex-1 overflow-auto custom-scrollbar"
        data-testid="task-table-body"
      >
        <div style={{ minWidth: totalWidth }}>
          {tasks.map((t) => {
            const isSubtask = !!t.parent_id;
            const childCount = childCountByParent.get(t.id) || 0;
            const hasChildren = childCount > 0;
            const isCollapsed = hasChildren && collapsed?.[t.id];
            return (
              <TaskRow
                key={t.id}
                task={t}
                allTasks={allTasks || tasks}
                numberingById={numbering}
                visibleBase={visibleBase}
                widthFor={widthFor}
                stickyLeftByKey={stickyLeftByKey}
                stickyEndLeft={stickyEndLeft}
                selectOffset={selectOffset}
                workstreamColors={project.workstream_colors || {}}
                statuses={statuses}
                displayNo={numbering.get(t.id)}
                isSubtask={isSubtask}
                hasChildren={hasChildren}
                childCount={childCount}
                isCollapsed={isCollapsed}
                dateCols={dateCols}
                customCols={customCols}
                canEdit={canEdit}
                onUpdate={(patch) => onUpdateTask(t.id, patch)}
                onDelete={() => onDeleteTask(t.id)}
                onAddSubtask={(names) => onAddSubtask?.(t.id, names)}
                onToggleCollapse={() => onToggleCollapse?.(t.id)}
                flash={flashTaskId === t.id}
                critical={criticalIds?.has(t.id)}
                selectMode={selectMode}
                selected={!!selectedIds?.has(t.id)}
                onToggleSelect={() => onToggleSelect?.(t.id)}
                palette={palette}
                onOpenDetail={onOpenDetail}
                onReorderRows={onReorderRows}
                projectIdForLink={project?.id}
                meetingMarkers={meetingMarkersByTask[t.id] || []}
              />
            );
          })}

          {tasks.length === 0 && (
            <div className="p-12 text-center text-sm text-slate-500" data-testid="empty-tasks">
              {allTasksCount === 0 ? "No tasks yet." : "No tasks match the current filters."}
              {canEdit && allTasksCount === 0 && (
                <div className="mt-4">
                  <button
                    onClick={onAddTask}
                    className="inline-flex items-center gap-2 px-3 h-8 bg-slate-950 text-white rounded-md text-xs font-medium hover:bg-slate-800"
                    data-testid="empty-add-task-btn"
                  >
                    <Plus size={12} weight="bold" /> Add first task
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Add-task footer — pinned outside the scroll body so it's
          always visible even when the task list runs long. Hidden when the
          list is empty (the empty state has its own CTA above). */}
      {tasks.length > 0 && canEdit && (
        <button
          type="button"
          onClick={onAddTask}
          className="shrink-0 flex items-center gap-2 h-11 px-4 border-t border-slate-200 bg-white text-[13px] font-medium text-slate-600 hover:text-slate-950 hover:bg-slate-50 transition-colors group"
          data-testid="add-task-row-btn"
        >
          <span
            className="inline-flex items-center justify-center h-5 w-5 rounded-md border border-dashed border-slate-400 group-hover:border-slate-950 group-hover:bg-slate-950 group-hover:text-white transition-colors"
          >
            <Plus size={11} weight="bold" />
          </span>
          <span className="tracking-tight">Add task</span>
          <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400 group-hover:text-slate-500">
            appends to the bottom of the list
          </span>
        </button>
      )}
    </div>
  );
}

// Header cell with a draggable right-edge that resizes the column. Supports sticky positioning
// (so No/Task/Subtask can stay pinned) and optional double-click rename for non-required columns.
function ResizableHeader({
  width,
  minWidth = 60,
  align,
  flexCol,
  onResize,
  testid,
  children,
  stickyLeft,
  shadowOnRight,
  onRename,
  renameTestid,
  bg = "white",
  isDark = false,
}) {
  const startRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(typeof children === "string" ? children : "");

  // When children change (parent renamed), keep local buffer in sync.
  useEffect(() => {
    if (typeof children === "string") setVal(children);
  }, [children]);

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startRef.current = { x: e.clientX, w: width };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      if (!startRef.current) return;
      const dx = ev.clientX - startRef.current.x;
      const next = Math.max(minWidth, startRef.current.w + dx);
      onResize?.(next);
    };
    const onUp = () => {
      startRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const commitRename = () => {
    setEditing(false);
    const next = (val || "").trim();
    const current = typeof children === "string" ? children : "";
    if (next && next !== current) onRename?.(next);
    else setVal(current);
  };

  const style = { width, flexShrink: 0 };
  if (stickyLeft != null) {
    style.position = "sticky";
    style.left = stickyLeft;
    style.zIndex = 2;
    style.background = bg;
  }

  return (
    <div
      className={`relative flex ${flexCol ? "flex-col" : "items-end"} px-3 pb-2.5 pt-3 border-r border-slate-200 ${align === "center" ? "justify-center text-center" : "justify-start"} group/col shrink-0`}
      style={style}
    >
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") {
              setEditing(false);
              setVal(typeof children === "string" ? children : "");
            }
          }}
          className="h-6 px-1 rounded-md border border-slate-950 text-[10px] uppercase tracking-widest font-mono-ibm font-medium text-slate-700 outline-none w-[80%]"
          data-testid={renameTestid}
        />
      ) : (
        <button
          type="button"
          onDoubleClick={() => onRename && setEditing(true)}
          className={`text-left inline-flex items-center max-w-full truncate ${onRename ? "cursor-text hover:text-slate-950" : "cursor-default"}`}
          title={onRename ? "Double-click to rename" : undefined}
          data-testid={renameTestid ? `${renameTestid}-trigger` : undefined}
        >
          <span className="truncate">{children}</span>
        </button>
      )}
      {shadowOnRight && (
        <div
          className="absolute right-0 top-0 bottom-0 w-2 pointer-events-none"
          aria-hidden="true"
          style={{
            background: isDark
              ? "linear-gradient(90deg, rgba(0,0,0,0.45), rgba(0,0,0,0))"
              : "linear-gradient(90deg, rgba(15,23,42,0.04), rgba(15,23,42,0))",
          }}
        />
      )}
      <button
        type="button"
        onMouseDown={onMouseDown}
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-slate-950/20 group-hover/col:bg-slate-950/10 z-[3]"
        aria-label="Resize column"
        data-testid={testid}
        title="Drag to resize"
      />
    </div>
  );
}



function TaskRow({
  task,
  allTasks,
  numberingById,
  visibleBase,
  widthFor,
  stickyLeftByKey,
  stickyEndLeft,
  selectOffset,
  workstreamColors,
  statuses,
  displayNo,
  isSubtask,
  hasChildren,
  childCount,
  isCollapsed,
  dateCols,
  customCols,
  canEdit,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleCollapse,
  flash,
  critical,
  selectMode,
  selected,
  onToggleSelect,
  palette,
  onOpenDetail,
  onReorderRows,
  projectIdForLink,
  meetingMarkers = [],}) {
  const baseKeys = new Set(visibleBase.map((c) => c.key));
  const showCol = (key) => baseKeys.has(key);
  const w = (key, fallback) => widthFor(key, fallback);

  // Each row's sticky cells need a solid bg so scrolling content doesn't bleed through.
  // We match the row state (selected > flash > critical > subtask > default) so the
  // pinned area visually belongs to the row it's part of. Palette is theme-aware
  // (passed down from the parent's useTheme call).
  const p = palette || { default: "#ffffff", subtask: "#f8fafc", critical: "#fef2f2", flash: "#fffbeb", selected: "#f1f5f9" };
  const rowBg = selected
    ? p.selected
    : flash
      ? p.flash
      : critical
        ? p.critical
        : isSubtask
          ? p.subtask
          : p.default;
  const stickyStyleFor = (key) => {
    const left = stickyLeftByKey?.[key];
    if (left == null) return undefined;
    return { position: "sticky", left, zIndex: 1, background: rowBg };
  };

  // Drag indicator state from parent: if this row is the current drop
  // target, paint the visual cue (top/bottom line for reorder, ring for
  // nest). Rendered inside the row container below.
  const indicator = onReorderRows?.indicator;
  const isDropTarget = indicator?.targetId === task.id;
  const showTopLine = isDropTarget && indicator?.mode === "reorder-before";
  const showBottomLine = isDropTarget && indicator?.mode === "reorder-after";
  const showNestRing = isDropTarget && indicator?.mode === "nest";

  return (
    <div
      className={`flex h-10 border-b border-slate-100 hover:bg-slate-50/80 text-sm group relative transition-colors ${flash ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""} ${critical ? "bg-red-50/40" : ""} ${isSubtask ? "bg-slate-50/60" : ""} ${selected ? "bg-slate-950/[0.04] ring-1 ring-inset ring-slate-950/20" : ""} ${showNestRing ? "ring-2 ring-inset ring-rose-500 bg-rose-50/70" : ""}`}
      data-testid={`task-row-${task.id}`}
      data-parent={task.parent_id || ""}
      // Drop targets live on the whole row so the user can release anywhere
      // over a row. Parent decides reorder-vs-nest by cursor X position.
      onDragOver={canEdit && onReorderRows ? (e) => onReorderRows.over(e, task.id) : undefined}
      onDrop={canEdit && onReorderRows ? (e) => onReorderRows.drop(e, task.id) : undefined}
    >
      {/* Reorder indicator line — sits ABOVE the row (before) or BELOW it
          (after). 2px bright blue for high contrast. */}
      {showTopLine && (
        <div
          className="absolute -top-px left-0 right-0 h-0.5 bg-sky-500 z-10 pointer-events-none"
          data-testid={`drop-indicator-before-${task.id}`}
        />
      )}
      {showBottomLine && (
        <div
          className="absolute -bottom-px left-0 right-0 h-0.5 bg-sky-500 z-10 pointer-events-none"
          data-testid={`drop-indicator-after-${task.id}`}
        />
      )}
      {/* Nest indicator — vertical red bar on the right edge signaling
          "drop here to make a subtask of this row". */}
      {showNestRing && (
        <div
          className="absolute top-0 bottom-0 right-0 w-1 bg-rose-500 z-10 pointer-events-none"
          data-testid={`drop-indicator-nest-${task.id}`}
        />
      )}
      {critical && (
        <div
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-red-600"
          aria-hidden="true"
          data-testid={`critical-indicator-${task.id}`}
        />
      )}

      {selectMode && (
        <div
          className="flex items-center justify-center px-2 border-r border-slate-100 shrink-0"
          style={{ width: 34, position: "sticky", left: 0, zIndex: 2, background: rowBg }}
        >
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect?.()}
            aria-label={`Select ${task.task || task.subtask || "task"}`}
            className="h-3.5 w-3.5 rounded-[3px]"
            data-testid={`row-select-${task.id}`}
          />
        </div>
      )}

      {showCol("no") && (
        <Cell
          width={w("no", 46)}
          className="justify-center text-slate-400 font-mono-ibm text-xs"
          style={stickyStyleFor("no")}
        >
          <div className="flex items-center gap-1">
            <span>{displayNo}</span>
            {meetingMarkers.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const latest = meetingMarkers[0];
                  if (latest?.session_id && !latest.is_expired) {
                    if (typeof window !== "undefined") window.location.assign(`/standup/session/${latest.session_id}/summary`);
                  } else {
                    alert("This standup's notes have expired (30-day retention).");
                  }
                }}
                title={`${meetingMarkers.length} standup${meetingMarkers.length === 1 ? "" : "s"} on this task`}
                className="grid place-items-center h-4 w-4 rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200"
                data-testid={`meeting-marker-${task.id}`}
              >
                <UsersThree size={8} weight="bold" />
              </button>
            )}
          </div>
        </Cell>
      )}

      {showCol("task") && (
        <Cell width={w("task", 180)} style={stickyStyleFor("task")}>
          <div className="flex items-center gap-1 w-full min-w-0">
            {hasChildren ? (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="h-5 w-5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 flex items-center justify-center shrink-0"
                data-testid={`toggle-children-${task.id}`}
                title={isCollapsed ? `Expand ${childCount} subtask${childCount === 1 ? "" : "s"}` : "Collapse subtasks"}
              >
                {isCollapsed ? <CaretRight size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
              </button>
            ) : null}
            {/* Drag-handle — works on parent rows AND subtasks. The handle
                is the drag *source*; drop target is the row container.
                Reorder vs nest is decided by cursor X in the over handler. */}
            {canEdit && onReorderRows && (
              <span
                className="opacity-0 group-hover:opacity-100 w-0 group-hover:w-4 overflow-hidden h-5 flex items-center justify-center cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-950 shrink-0 transition-all duration-100"
                draggable
                onDragStart={(e) => onReorderRows.start(e, task.id)}
                onDragEnd={onReorderRows.end}
                data-testid={`reorder-handle-${task.id}`}
                title="Drag to reorder · drop on right half of another row to nest as subtask"
              >
                <DotsSixVertical size={12} weight="bold" />
              </span>
            )}
            {/* Eye / view-detail — also collapses on idle so titles get the
                full available width. */}
            {onOpenDetail && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenDetail(task.id); }}
                className="opacity-0 group-hover:opacity-100 w-0 group-hover:w-5 overflow-hidden h-5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-950 dark:hover:text-neon flex items-center justify-center shrink-0 transition-all duration-100"
                data-testid={`open-task-detail-${task.id}`}
                title="View task detail"
              >
                <Eye size={12} weight="bold" />
              </button>
            )}
            {isSubtask ? (
              <span className="text-slate-300 text-xs select-none" aria-hidden="true">—</span>
            ) : (
              <InlineEditable
                value={task.task}
                canEdit={canEdit}
                strong
                placeholder="Task"
                onSave={(v) => onUpdate({ task: v })}
                testid={`cell-task-${task.id}`}
              />
            )}
          </div>
        </Cell>
      )}

      {showCol("subtask") && (
        <Cell width={w("subtask", 220)} style={stickyStyleFor("subtask")}>
          <div className="flex items-center gap-1 w-full min-w-0" style={{ paddingLeft: isSubtask ? 14 : 0 }}>
            {isSubtask && (
              <span className="text-slate-400 font-mono-ibm text-xs shrink-0 select-none" aria-hidden="true">
                ↳
              </span>
            )}
            {/* Eye / view-detail for subtasks (parents have it on the task cell) */}
            {isSubtask && onOpenDetail && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenDetail(task.id); }}
                className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-950 dark:hover:text-neon flex items-center justify-center shrink-0 transition-opacity"
                data-testid={`open-task-detail-${task.id}`}
                title="View task detail"
              >
                <Eye size={12} weight="bold" />
              </button>
            )}
            {isSubtask ? (
              <InlineEditable
                value={task.subtask}
                canEdit={canEdit}
                placeholder="Subtask"
                onSave={(v) => onUpdate({ subtask: v })}
                testid={`cell-subtask-${task.id}`}
              />
            ) : (
              // Parent row's Subtask cell:
              //   - show free-text subtask (if any) as inline-editable
              //   - show "N subtasks" badge (if children exist)
              //   - ALWAYS show the "+ Add subtask" trigger so the action is discoverable
              //     even when the legacy `task.subtask` field is non-empty (e.g. CSV
              //     imported a literal "—" placeholder).
              <div className="flex items-center gap-1.5 min-w-0 w-full">
                {task.subtask && task.subtask.trim() && task.subtask.trim() !== "—" && task.subtask.trim() !== "-" ? (
                  <InlineEditable
                    value={task.subtask}
                    canEdit={canEdit}
                    placeholder="Subtask"
                    onSave={(v) => onUpdate({ subtask: v })}
                    testid={`cell-subtask-${task.id}`}
                  />
                ) : null}
                {hasChildren && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-[11px] font-mono-ibm shrink-0">
                    {childCount} {childCount === 1 ? "subtask" : "subtasks"}
                  </span>
                )}
                {canEdit && (
                  <BulkSubtaskAdd
                    compact={hasChildren || (task.subtask && task.subtask.trim() && task.subtask.trim() !== "—" && task.subtask.trim() !== "-")}
                    alwaysVisible
                    onAdd={(names) => onAddSubtask?.(names)}
                    testid={`inline-add-subtask-${task.id}`}
                  />
                )}
                {!canEdit && !task.subtask && !hasChildren && (
                  <span className="text-slate-300">—</span>
                )}
              </div>
            )}
          </div>
        </Cell>
      )}

      {showCol("workstream") && (
        <Cell width={w("workstream", 130)}>
          {canEdit ? (
            <InlineEditable
              inline
              value={task.workstream}
              canEdit={canEdit}
              onSave={(v) => onUpdate({ workstream: v })}
              placeholder="Workstream"
              testid={`cell-workstream-${task.id}`}
              renderDisplay={(v) =>
                v ? <WorkstreamBadge name={v} overrides={workstreamColors} /> : <span className="text-slate-400">—</span>
              }
            />
          ) : task.workstream ? (
            <WorkstreamBadge name={task.workstream} overrides={workstreamColors} />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </Cell>
      )}

      {showCol("status") && (
        <Cell width={w("status", 140)}>
          {canEdit ? (
            <Select value={task.status || ""} onValueChange={(v) => onUpdate({ status: v })}>
              <SelectTrigger
                className="h-6 text-[11px] font-medium border rounded-md px-2 py-0 focus:ring-0"
                style={statusPillStyle(statuses, task.status)}
                data-testid={`cell-status-${task.id}`}
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                {statuses.map((s) => (
                  <SelectItem key={s.id} value={s.label} className="text-xs" data-testid={`status-option-${s.id}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                      {s.label}
                    </span>
                  </SelectItem>
                ))}
                {task.status && !statuses.some((s) => s.label === task.status) && (
                  <SelectItem value={task.status} className="text-xs">{task.status}</SelectItem>
                )}
              </SelectContent>
            </Select>
          ) : task.status ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border"
              style={statusPillStyle(statuses, task.status)}
            >
              {task.status}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </Cell>
      )}

      {showCol("dependencies") && (
        <DependencyPicker
          task={task}
          allTasks={allTasks}
          numberingById={numberingById}
          canEdit={canEdit}
          onChange={(patch) => onUpdate(patch)}
          width={w("dependencies", 200)}
        />
      )}

      {dateCols.map((col) => (
        <EditableDateCell
          key={col.id}
          width={w(`date:${col.id}`, 110)}
          value={(task.dates || {})[col.id]}
          canEdit={canEdit}
          onSave={(d) => onUpdate({ dates: { [col.id]: d } })}
          testid={`cell-date-${col.id}-${task.id}`}
        />
      ))}

      {(customCols || []).map((col) => (
        <CustomFieldCell
          key={`cust-${col.id}`}
          width={w(`custom:${col.id}`, 140)}
          col={col}
          allTasks={allTasks}
          numberingById={numberingById}
          value={(task.custom_fields || {})[col.id]}
          canEdit={canEdit}
          onSave={(v) => onUpdate({ custom_fields: { ...(task.custom_fields || {}), [col.id]: v } })}
          testid={`cell-custom-${col.id}-${task.id}`}
        />
      ))}

      <div className="w-9 shrink-0 border-r border-slate-100 flex items-center justify-center opacity-0 group-hover:opacity-100">
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-6 w-6 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-950 flex items-center justify-center"
                data-testid={`task-actions-${task.id}`}
              >
                <DotsThreeVertical size={14} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-md">
              {!isSubtask && (
                <DropdownMenuItem onClick={onAddSubtask} data-testid={`add-subtask-${task.id}`}>
                  <TreeStructure size={12} weight="bold" className="mr-2" /> Add subtask
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  // Drop straight into the StandUp setup wizard with this
                  // task pre-linked. Phase 4 will surface the meeting icon on
                  // this Gantt row once the session ends and the marker
                  // writeback lands.
                  const qs = new URLSearchParams({
                    name: `${task.label || "Task"} standup`,
                    cadence: "daily",
                    linked_project: projectIdForLink || "",
                    linked_task: task.id || "",
                  }).toString();
                  if (typeof window !== "undefined") window.location.assign(`/standup/new?${qs}`);
                }}
                data-testid={`schedule-standup-${task.id}`}
              >
                <UsersThree size={12} weight="bold" className="mr-2" /> Schedule standup for this task
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                data-testid={`delete-task-${task.id}`}
              >
                <Trash size={12} weight="bold" className="mr-2" /> Delete {isSubtask ? "subtask" : "task"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function Cell({ width, className = "", children, style }) {
  return (
    <div
      className={`flex items-center px-3 border-r border-slate-100 shrink-0 ${className}`}
      style={{ width, ...(style || {}) }}
    >
      {children}
    </div>
  );
}

// Compact workstream label + dot. Respects project-level colour overrides when present.
function WorkstreamBadge({ name, overrides }) {
  const meta = workstreamColor(name, overrides);
  if (meta.overrideHex) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium"
        style={{ background: `${meta.overrideHex}22`, color: meta.overrideHex }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.overrideHex }} />
        {name}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${meta.bg} ${meta.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {name}
    </span>
  );
}

function InlineEditable({
  cellWidth,
  value,
  canEdit,
  onSave,
  placeholder = "",
  strong = false,
  mono = false,
  testid,
  inline = false,
  renderDisplay,
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => { setVal(value || ""); }, [value]);

  const commit = () => {
    setEditing(false);
    if ((val || "") !== (value || "")) onSave(val);
  };

  const displayNode = renderDisplay ? (
    renderDisplay(value)
  ) : value ? (
    <span className={`${strong ? "font-semibold text-slate-950" : "text-slate-700"} ${mono ? "font-mono-ibm text-xs" : ""} truncate`}>
      {value}
    </span>
  ) : (
    <span className="text-slate-400">{placeholder}</span>
  );

  const inner = editing ? (
    <input
      ref={inputRef}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setVal(value || ""); } }}
      autoFocus
      className="w-full h-7 px-1.5 rounded-md border border-slate-950 bg-white outline-none text-sm"
      data-testid={testid}
    />
  ) : (
    <button
      type="button"
      onClick={() => canEdit && setEditing(true)}
      className={`text-left w-full truncate ${canEdit ? "cursor-text hover:bg-slate-100 rounded-md px-1 -mx-1 py-0.5" : ""}`}
      data-testid={testid}
    >
      {displayNode}
    </button>
  );

  if (inline) return inner;
  if (cellWidth) return <Cell width={cellWidth}>{inner}</Cell>;
  return inner;
}

function EditableDateCell({ width, value, canEdit, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");

  useEffect(() => { setVal(value || ""); }, [value]);

  const commit = () => {
    setEditing(false);
    if ((val || null) !== (value || null)) onSave(val || null);
  };

  // When a user pastes a non-ISO date (e.g. "02/04/2026" or "Feb 4, 2026") the
  // browser's <input type=date> would reject it. We normalise to YYYY-MM-DD.
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text")?.trim();
    if (!text) return;
    const iso = toIsoDate(text);
    if (iso) {
      e.preventDefault();
      setVal(iso);
    }
  };

  return (
    <Cell width={width}>
      {editing ? (
        <input
          type="date"
          value={val || ""}
          onChange={(e) => setVal(e.target.value)}
          onPaste={handlePaste}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setVal(value || ""); } }}
          autoFocus
          className="w-full h-7 px-1.5 rounded-md border border-slate-950 bg-white outline-none text-xs font-mono-ibm"
          data-testid={testid}
        />
      ) : (
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          className={`text-left w-full truncate text-xs font-mono-ibm ${canEdit ? "cursor-text hover:bg-slate-100 rounded-md px-1 -mx-1 py-0.5" : ""} ${value ? "text-slate-700" : "text-slate-400"}`}
          data-testid={testid}
        >
          {value ? formatDate(value) : "—"}
        </button>
      )}
    </Cell>
  );
}

// Renders a single custom-field cell (text / enum / int / dependency).
// Click → edit; Enter or blur commits. For `enum` types we show a simple <select>.
function CustomFieldCell({ width, col, value, canEdit, onSave, allTasks, numberingById, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");

  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  const commit = () => {
    setEditing(false);
    let next = val;
    if (col.type === "int") {
      const n = parseInt(val, 10);
      next = Number.isFinite(n) ? n : "";
    }
    if ((next ?? "") !== (value ?? "")) onSave(next === "" ? null : next);
  };

  if (col.type === "dependency") {
    // For dependency-type custom columns we resolve the stored task id → its display name.
    const target = allTasks?.find((t) => t.id === value);
    const num = numberingById?.get(value);
    return (
      <Cell width={width}>
        <span className="text-xs text-slate-700 dark:text-slate-200 truncate" data-testid={testid}>
          {target ? `${num || "?"} · ${target.task || target.subtask || "Untitled"}` : (value ? "(missing)" : "—")}
        </span>
      </Cell>
    );
  }

  if (editing && col.type === "enum") {
    return (
      <Cell width={width}>
        <select
          autoFocus
          value={val || ""}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          className="w-full h-7 px-1.5 rounded-md border border-slate-950 bg-white dark:bg-slate-900 dark:text-mint outline-none text-xs"
          data-testid={testid}
        >
          <option value="">—</option>
          {(col.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </Cell>
    );
  }

  if (editing) {
    return (
      <Cell width={width}>
        <input
          type={col.type === "int" ? "number" : "text"}
          autoFocus
          value={val ?? ""}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setEditing(false);
              setVal(value ?? "");
            }
          }}
          className="w-full h-7 px-1.5 rounded-md border border-slate-950 bg-white dark:bg-slate-900 dark:text-mint outline-none text-sm"
          data-testid={testid}
        />
      </Cell>
    );
  }

  const display = value === undefined || value === null || value === "" ? "—" : String(value);
  return (
    <Cell width={width}>
      <button
        type="button"
        onClick={() => canEdit && setEditing(true)}
        className={`text-left w-full truncate text-xs ${canEdit ? "cursor-text hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md px-1 -mx-1 py-0.5" : ""} ${value ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}`}
        data-testid={testid}
        title={display}
      >
        {display}
      </button>
    </Cell>
  );
}

function BulkSubtaskAdd({ onAdd, testid, compact = false, alwaysVisible = false }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [pending, setPending] = useState([]); // chips queued for commit
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  // Recompute panel position on resize/scroll while open so it tracks the cell.
  useEffect(() => {
    if (!open || !coords?.anchor) return;
    const reposition = () => {
      const r = coords.anchor.getBoundingClientRect();
      setCoords((c) => ({ ...c, ...computeCoords(r) }));
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, coords?.anchor]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        // Auto-commit any queued/typed values when clicking outside, instead of discarding.
        commitAll();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pending, value]);

  useEffect(() => {
    if (open && ref.current) ref.current.focus();
  }, [open]);

  const computeCoords = (r) => {
    const PANEL_W = 340;
    const MARGIN = 12;
    const left = Math.max(MARGIN, Math.min(r.left, window.innerWidth - PANEL_W - MARGIN));
    const top = Math.min(r.bottom + 6, window.innerHeight - 240);
    return { top, left };
  };

  const handleOpen = (e) => {
    const anchor = e.currentTarget;
    const r = anchor.getBoundingClientRect();
    setCoords({ ...computeCoords(r), anchor });
    setOpen(true);
  };

  const cancel = () => {
    setOpen(false);
    setValue("");
    setPending([]);
  };

  // Push the typed value (and any pasted multi-line content) into the chip list.
  const stashCurrent = () => {
    const fresh = value
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (fresh.length === 0) return pending;
    const next = [...pending, ...fresh];
    setPending(next);
    setValue("");
    return next;
  };

  const commitAll = () => {
    const all = stashCurrent();
    if (all.length > 0) onAdd(all);
    setOpen(false);
    setValue("");
    setPending([]);
  };

  const removePending = (idx) => {
    setPending((p) => p.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // If the user is rapid-firing single-line subtasks, Enter stashes the current value
      // as a chip and clears the input so they can keep typing the next one.
      if ((e.metaKey || e.ctrlKey)) {
        commitAll();
      } else {
        stashCurrent();
      }
    }
  };

  // Trigger button — visible by default for parents (alwaysVisible) so the action is obvious.
  const trigger = compact ? (
    <button
      type="button"
      onClick={handleOpen}
      className={`${alwaysVisible ? "" : "opacity-0 group-hover:opacity-100"} h-5 w-5 rounded hover:bg-slate-950 hover:text-white dark:hover:bg-neon dark:hover:text-ink text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-700 flex items-center justify-center transition-opacity`}
      title="Add subtasks — type & Enter to chain, paste a list to bulk-add"
      data-testid={testid}
    >
      <Plus size={10} weight="bold" />
    </button>
  ) : (
    <button
      type="button"
      onClick={handleOpen}
      className={`${alwaysVisible ? "" : "opacity-0 group-hover:opacity-100"} inline-flex items-center gap-1.5 h-6 px-2 rounded-md border border-dashed border-slate-300 dark:border-slate-700 hover:border-slate-950 dark:hover:border-neon hover:bg-slate-50 dark:hover:bg-slate-800/40 text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-950 dark:hover:text-neon transition-all`}
      data-testid={testid}
    >
      <Plus size={10} weight="bold" />
      Add subtask
    </button>
  );

  if (!open || !coords) return trigger;

  const totalQueued = pending.length + (value.trim() ? 1 : 0);

  return (
    <>
      {trigger}
      {createPortal(
        <div
          ref={panelRef}
          className="fixed w-[340px] bg-white dark:bg-[#15101a] border border-slate-950 dark:border-neon rounded-md shadow-[0_12px_40px_rgba(15,23,42,0.25)] p-2 fade-up"
          style={{ top: coords.top, left: coords.left, zIndex: 9999 }}
          data-testid={`${testid}-panel`}
        >
          {pending.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2 max-h-[140px] overflow-y-auto custom-scrollbar pr-1">
              {pending.map((name, idx) => (
                <span
                  key={`${name}-${idx}`}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-mint text-[11px] font-medium"
                  data-testid={`${testid}-chip-${idx}`}
                >
                  <span className="truncate max-w-[200px]">{name}</span>
                  <button
                    type="button"
                    onClick={() => removePending(idx)}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`${testid}-chip-remove-${idx}`}
                    aria-label="Remove pending subtask"
                  >
                    <Plus size={10} weight="bold" className="rotate-45" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={ref}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={(e) => {
              const text = e.clipboardData?.getData("text") || "";
              if (text.includes("\n")) {
                e.preventDefault();
                const fresh = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
                if (fresh.length > 0) setPending((p) => [...p, ...fresh]);
              }
            }}
            placeholder="Type a subtask, press Enter to add another"
            className="w-full h-8 text-sm font-sans text-slate-900 dark:text-mint bg-transparent outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600 px-2 border border-slate-200 dark:border-slate-700 rounded-md focus:border-slate-950 dark:focus:border-neon"
            data-testid={`${testid}-textarea`}
          />
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">
              {totalQueued === 0 ? "Enter to chain · paste = bulk" : `${totalQueued} queued · ⌘/Ctrl+Enter to commit`}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={cancel}
                className="h-6 px-2 text-[11px] text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-neon rounded"
                data-testid={`${testid}-cancel`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitAll}
                disabled={totalQueued === 0}
                className="h-6 px-2.5 text-[11px] font-medium rounded bg-slate-950 text-white hover:bg-slate-800 dark:bg-neon dark:text-ink dark:hover:bg-[#b8de00] disabled:opacity-40"
                data-testid={`${testid}-commit`}
              >
                Add {totalQueued > 0 ? totalQueued : ""}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
